<template>
  <DpPanel>
    <template v-slot:header>
      <div v-if="$route.params.error === 'pets-missing'" class="alert-warning alert">
        Sorry, we're having trouble adding your pet. Please try adding your pet again.
      </div>

      <h1 class="text-center">{{ $t('petPanel.aboutYourPet') }}</h1>
      <p class="text-center" v-html="$t('petPanel.petPanelInfo')"></p>

      <div class="sub-header" v-if="pets.length <= 0 && (getCityID() === '3' || noLicensing())">
        <p>
          {{ $t('petPanel.dontWantToAddPet') }}
          <b-link @click="skipThisStep" class="text-decoration-underline">{{ $t('petPanel.skipThisStep') }}</b-link>
        </p>
      </div>
      <div class="sub-header" v-else-if="pets.length > 0">
        <p>
          {{ $t('petPanel.doneAddingPet') }}
          <b-link @click="skipThisStep" class="text-decoration-underline">{{ $t('petPanel.skipThisStep') }}</b-link>
        </p>
      </div>
      <div class="border-bottom my-4" v-else />
    </template>
    <template v-slot:content>
      <b-row class="mb-2">
        <b-col>
          <dp-add-a-pet-form
            ref="form"
            :pet="pet"
            :all-breeds="breeds"
            :existing-name="existingName"
            :existing-pet-names="getAllActivePetNames"
            :inactive-pet-names="getAllInactivePetNames"
            :customer-support-phone="getCustomerSupportPhone"
            @addPet="addPetAction"
            @editPet="editPetAction"
          />
        </b-col>
      </b-row>
    </template>
  </DpPanel>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import 'vue-simple-suggest/dist/styles.css';
import { DpPanel, DpAddAPetForm } from '@docupet/component-library';

const { mapState: mapRegisterState, mapGetters: mapRegisterGetters, mapActions: mapRegisterActions } = createNamespacedHelpers('register');

const { mapActions: mapCityActions, mapGetters: mapCityGetters } = createNamespacedHelpers('city');
const { mapState, mapActions } = createNamespacedHelpers('petManagement');

export default {
  name: 'AboutYourPet',
  components: {
    DpPanel,
    DpAddAPetForm,
  },
  data() {
    return {
      existingName: '',
    };
  },
  methods: {
    ...mapCityActions(['loadCity']),
    ...mapCityGetters(['getCityID', 'noLicensing']),
    ...mapRegisterActions(['addPet', 'editPet', 'storePet']),
    ...mapRegisterGetters(['error']),
    ...mapActions(['getBreeds']),
    async loadCityData() {
      await this.loadCity();
    },

    skipThisStep() {
      this.$router.push({
        name: 'ContactDetails',
      });
    },

    async addPetAction(petFormData) {
      try {
        const success = await this.addPet(petFormData);

        if (!success) {
          return;
        }

        window?.dataLayer?.push({ event: 'pet_profile' });

        const uuid = this.pet.uuid;

        await this.storePet();

        const redirectUrl = this.$router.resolve({name: 'AddAnotherPet'}).href;
        await this.$router.replace({
          name: 'AddPetImage',
          params: {
            uuid: uuid,
          },
          query: {
            redirect: redirectUrl,
          },
        });
      } catch (e) {
        if (['account_with_same_pet', 'address_with_same_pet'].includes(e.name)) {
          this.$refs.form?.$refs?.form?.setErrors({
            'pet-name': e.message,
          });
        }
      }
    },

    editPetAction({ petFormData, petId }) {
      // Convert our form object to expecting PetDetailsInput
      let inputObject = {
        breed: petFormData.breed,
        name: petFormData.name,
        birthday: petFormData.birthdate,
        age: petFormData.age,
        color: petFormData.colors,
        type: petFormData.type === 'C' ? 'CAT' : 'DOG',
        indoorAnimal: petFormData.isIndoor === '' ? false : Boolean(petFormData.isIndoor),
      };

      let petData = {};

      petData.form = petFormData;
      petData.details = inputObject;
      petData.pet = petId;

      this.editPet(petData).then((success) => {
        if (success && this.pet) {
          const redirectUrl = this.$router.resolve({name: 'AddAnotherPet'}).href;
          this.$router.replace({
            name: 'AddPetImage',
            params: {
              uuid: this.pet.uuid,
            },
            query: {
              redirect: redirectUrl,
            },
          });
        }
      });
    },
  },
  mounted: function () {
    this.getBreeds();
    this.loadCityData();

    // TODO: Investigate if I need this
    let urlParams;

    (window.onpopstate = function () {
      var match,
        pl = /\+/g, // Regex for replacing addition symbol with a space
        search = /([^&=]+)=?([^&]*)/g,
        decode = function (s) {
          return decodeURIComponent(s.replace(pl, ' '));
        },
        query = window.location.hash.substring(1);

      urlParams = {};
      while ((match = search.exec(query))) urlParams[decode(match[1])] = decode(match[2]);
    })();

    if (urlParams) {
      let tokenKey = '';

      Object.keys(urlParams).forEach((param) => {
        if (param.includes('token')) {
          tokenKey = param;
        }
      });

      if (urlParams[tokenKey]) {
        // this should in theory be committed to the store.
        window.token = urlParams[tokenKey];
      }
    }
  },
  computed: {
    ...mapCityGetters(['getCustomerSupportPhone']),
    ...mapRegisterGetters(['getAllActivePetNames', 'getAllInactivePetNames']),
    ...mapRegisterState({
      pet: (state) => state.pet,
      pets: (state) => state.pets,
    }),
    ...mapState({
      catBreeds: (state) => state.breedsCat,
      dogBreeds: (state) => state.breedsDog,
    }),
    breeds() {
      return { catBreeds: this.catBreeds, dogBreeds: this.dogBreeds };
    },
  },
};
</script>
